import { useQuery } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useState } from 'react';
import { connect } from 'react-redux';

import { gengql } from '@/__generated__';
import CognitoMfaSettings from '@/auth/components/CognitoMfaSettings/CognitoMfaSettings';
import ConnectedAccounts from '@/auth/components/ConnectedAccounts/ConnectedAccounts';
import DisableOtpAuth from '@/auth/components/SecurityDisableOtpAuth';
import { EnableOtpAuth, FAQ_URL } from '@/auth/components/SecurityEnableOtpAuth';
import {
  SOCIAL_TWO_FACTOR_NOTE,
  isSocialSignInEnabled,
} from '@/auth/components/SocialSignInButtons/SocialSignInButtons';
import { createOtpAuthConfiguration } from '@/auth/store';
import { isCognitoLoginEnabled, isCognitoSessionActive } from '@/auth/utils/cognitoSession';
import Checkbox from '@/components/Checkbox';
import FaqLink from '@/components/FaqLink';
import SettingsSection from '@/components/SettingsSection';
import { useAppSelector } from '@/store';

const GET_OTP_CONFIGURATION = gengql(/* GraphQL */ `
  query securityGetOtpConfig {
    otpConfiguration {
      enabled
    }
  }
`);

type OTPConfiguration = {
  enabled: boolean;
};

const useStyles = makeStyles({
  section: {
    margin: '0 !important',
  },
  faq: {
    paddingTop: 20,
  },
});

function Security({ onDisableOtpAuth, user }: any) {
  const s = useStyles();

  const [enableOtpAuthOpen, setEnableOtpAuthOpen] = useState(false);
  const [disableOtpAuthOpen, setDisableOtpAuthOpen] = useState(false);

  const { data, refetch } = useQuery<{ otpConfiguration: OTPConfiguration }>(GET_OTP_CONFIGURATION);
  const otpAuthEnabled = data?.otpConfiguration?.enabled || false;

  const handleEnabled = useCallback(async () => {
    if (otpAuthEnabled) {
      setDisableOtpAuthOpen(true);
    } else {
      setEnableOtpAuthOpen(true);
    }
  }, [otpAuthEnabled]);

  const handleEnableOtpAuthClose = useCallback(() => {
    setEnableOtpAuthOpen(false);
  }, []);

  const handleDisableOtpAuthClose = useCallback(() => {
    setDisableOtpAuthOpen(false);
  }, []);

  return (
    <div>
      <SettingsSection className={s.section} box title="Two-factor verification">
        {isSocialSignInEnabled() && (
          <p className="mb-12 text-on-surface-secondary">{SOCIAL_TWO_FACTOR_NOTE}</p>
        )}
        <Checkbox
          label="Enabled"
          checked={otpAuthEnabled}
          onClick={handleEnabled}
          FormControlProps={{ margin: 'none' }}
        />
      </SettingsSection>
      <FaqLink url={FAQ_URL} className={s.faq} />
      <DisableOtpAuth
        open={disableOtpAuthOpen}
        onClose={handleDisableOtpAuthClose}
        user={user}
        onDisable={onDisableOtpAuth}
        refetch={refetch}
      />
      <EnableOtpAuth
        open={enableOtpAuthOpen}
        onCancel={handleEnableOtpAuthClose}
        user={user}
        email={user.email.address}
        setOtp={() => {}}
        refetch={refetch}
      />
    </div>
  );
}

// @ts-expect-error TS(2630): Cannot assign to 'Security' because it is a functi... Remove this comment to see the full error message
Security = connect(
  (state, ownProps) => ({
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    user: state.users[ownProps.userId],
  }),
  {
    createOtpAuthConfiguration,
  }
)(Security);

interface SecurityFormProps {
  userId?: string;
  onDisableOtpAuth?: () => void;
  /** The profile dialog passes these; the legacy form ignores them. */
  maxWidth?: string;
  title?: string;
}

/**
 * ENG-6107: a viewer signed in through Cognito manages their own factors on
 * Cognito. Everyone else, and staff editing another user, keeps the legacy form.
 * ENG-6192: the viewer's own page lists their Google and Microsoft accounts
 * first, only on a Cognito session: a provider connected to a legacy account
 * could never sign it in.
 */
export default function SecurityForm(props: SecurityFormProps): JSX.Element {
  const viewerId = useAppSelector((state) => state.viewer.id);
  const ownCognitoPage =
    isCognitoLoginEnabled() && isCognitoSessionActive() && props.userId === viewerId;
  const twoFactor = ownCognitoPage ? (
    <CognitoMfaSettings userId={viewerId} />
  ) : (
    <Security {...props} />
  );
  return (
    <>
      {ownCognitoPage && isSocialSignInEnabled() && <ConnectedAccounts />}
      {twoFactor}
    </>
  );
}
